import { getEnvironment } from "../Config/utils/enviroment";
import { log as telemetryLog } from "../Telemetry/log";

type LogLevel = "debug" | "info" | "warn" | "error";

const environment = getEnvironment();
const isProd = environment.startsWith("prod");

// DHT-5546 — on a native production build, the RN SDK's `trackErrors` patch
// turns a `console.error` into a RUM error (source `console`), and the native
// Datadog logger ALSO forwards every error-level `DdLogs.error` to RUM (source
// `logger`). Writing both reported each `logger.error` twice in RUM. Skip the
// console write there; the log line and its forwarded RUM error remain.
// DHT-5565: web has no Datadog Logs SDK: the web telemetry log is a console
// shim, and browser RUM captures every `console.error`, so the same two writes
// were two RUM errors there too. Skipping the console write leaves the shim's single
// `X | payload={…}` line, the same text native reports. `__DEV__` builds and
// non-production keep their console output on every platform.
const skipConsole = isProd && !__DEV__;

const shouldLog = (level: LogLevel) => {
  if (isProd) {
    return level === "error";
  }
  return true;
};

const safeStringify = (value: unknown) => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const logToConsole = (level: LogLevel, message: string, payload?: unknown) => {
  const consolePayload =
    payload === undefined ? undefined : safeStringify(payload);
  switch (level) {
    case "error":
      // eslint-disable-next-line no-console
      console.error(message, consolePayload);
      break;
    case "warn":
      // eslint-disable-next-line no-console
      console.warn(message, consolePayload);
      break;
    case "info":
    case "debug":
    default:
      // eslint-disable-next-line no-console
      console.log(message, consolePayload);
      break;
  }
};

const logToTelemetry = (
  level: LogLevel,
  message: string,
  payload?: unknown,
) => {
  const suffix =
    payload === undefined ? "" : ` | payload=${safeStringify(payload)}`;
  const formattedMessage = `${message}${suffix}`;
  switch (level) {
    case "error":
      telemetryLog.error(formattedMessage);
      break;
    case "warn":
      telemetryLog.warn(formattedMessage);
      break;
    case "info":
      telemetryLog.info(formattedMessage);
      break;
    case "debug":
    default:
      telemetryLog.debug(formattedMessage);
      break;
  }
};

const log = (level: LogLevel, message: string, payload?: unknown) => {
  if (!shouldLog(level)) return;
  if (!skipConsole) logToConsole(level, message, payload);
  logToTelemetry(level, message, payload);
};

export const logger = {
  debug: (message: string, payload?: unknown) => log("debug", message, payload),
  info: (message: string, payload?: unknown) => log("info", message, payload),
  warn: (message: string, payload?: unknown) => log("warn", message, payload),
  error: (message: string, payload?: unknown) => log("error", message, payload),
  analytics: (eventName: string, payload?: Record<string, unknown>) =>
    log("info", `[analytics] ${eventName}`, payload),
};
